import React from 'react';
import * as Inovx from '@shared';
import UserForm from './UserForm';
import UserView from './UserView';
import UserSettings from './UserSettings';
import UserStore from '../stores/UserStore';
import UserActions from '../actions/UserActions';
import UserConstants from '../constants/UserConstants';
import UserPermissionNames from '../constants/UserPermissionNames';
import UserSystemPreferences from './UserSystemPreferences';
import AuthenticationModeEnum from '../constants/AuthenticationModeEnum';
import ExternalProviderEnum from '../constants/ExternalProviderEnum';


var SystemActions = Inovx.Shared.System.Actions;
var SystemStore = Inovx.Shared.System.Store;
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var PermissionService = Inovx.Shared.Permission.PermissionService;
var PreferencesActions = Inovx.Shared.Preferences.Actions;
var PreferencesStore = Inovx.Shared.Preferences.Store;

declare var InovxLoader: any;

interface UserProps {
}

interface UserState {
    allUsers?: IUser[];
    modalVisible?: boolean;
    isActionInProgress?: boolean;
    authenticationMode: string;
    externalProviderName: string;
    settingsShown?: boolean;
}

function getActionInProgress() {
    return SystemStore.isActionInProgress(UserConstants.USER_DELETE) || SystemStore.isActionInProgress(UserConstants.SETTINGS_GET)
        || SystemStore.isActionInProgress(UserConstants.BEGIN_EDIT_USER) || SystemStore.isActionInProgress(UserConstants.BEGIN_USER_ADD);
}

function getUserState(): UserState {
    return {
        allUsers: UserStore.getAll(),
        modalVisible: UserStore.getModalVisible(),
        isActionInProgress: getActionInProgress(),
        authenticationMode: UserStore.getSettings().AuthenticationMode,
        externalProviderName: UserStore.getSettings().ExternalProviderName,
        settingsShown: UserStore.getSettingsShown()
    };
}

class UserApp extends Inovx.Shared.ReactExt.BaseAppComponent<UserProps, UserState> {
    constructor(props: UserProps, context: any) {
        super('users', props, context);

        this.state = getUserState();
    }

    private _onChange = () => {
        this.setState(getUserState());
    }

    private _onSystemChange = () => {
        this.setState({
            isActionInProgress: getActionInProgress()
        });
    }

    private _hideModal = () => {
        if (this.state.settingsShown) {
            UserActions.hideSettings();
        } else {
            UserActions.finishEdit();
        }
    }

    private _beginEditUser = (e: Event, user: IUser) => {
        UserActions.beginEdit(user);
    }

    private _deleteUser = (e: Event, user: IUser) => {
        var messageObject = {
            message: InovxLoader.t('users:message.confirmDelete'),
            confirmCallback: function () {
                UserActions.delete(user, null);
            }
        };
        SystemActions.displayConfirmationMessage(messageObject);
    }

    private _showAddUser = () => {
        UserActions.beginAdd();
    }

    private _showSettings = () => {
        UserActions.getSettings(true);
    }

    private _synchUsers = () => {
        this.setState({ isActionInProgress: true } as any);
        UserActions.synchronizeLinkedGroups(() => {
            UserActions.getAllUsers(
                () => {
                    UserActions.getSettings(false, () => this._appLoaded = true);
                    this.setState({ isActionInProgress: false } as any);
                },
                (error: any) => {
                    this._handlePermissionError(error),
                        true;
                    this.setState({ isActionInProgress: false } as any);
                }
            );
        }, (error: any) => {
            this._handlePermissionError(error), true;
            this.setState({ isActionInProgress: false } as any);
        } );
    }

    protected _initialize = () => {
        UserActions.getAllUsers(
            () => UserActions.getSettings(false, () => this._appLoaded = true),
            (error: any) => this._handlePermissionError(error),
            true
        );
        UserActions.getCurrentUser(() => this._appLoaded = true);
    }

    private _createUserSystemPreferencesComponent = (props: any): any => {
        return React.createElement(UserSystemPreferences, props);
    }

    private _enableSystemPreferences = () => {
        var hasUserSettings = PermissionService.checkPermission(UserPermissionNames.USER_SETTINGS);
        var hasEntitlementPermission = PermissionService.checkPermission(UserPermissionNames.ENTITLEMENT_MANAGE);
        var hasRolePermission = PermissionService.checkPermission(UserPermissionNames.ROLE_MANAGE);
        var hasUserViewPermission = PermissionService.checkPermission(UserPermissionNames.USER_VIEW);
        var hasUserManagePermission = PermissionService.checkPermission(UserPermissionNames.USER_MANAGE);
        if (hasUserSettings && hasRolePermission && hasEntitlementPermission && (hasUserViewPermission || hasUserManagePermission))
            PreferencesActions.registerCreateUserSystemPreferencesComponent(this._createUserSystemPreferencesComponent);
    }

    componentWillMount() {

        this.componentWillMountBase();
    }

    componentDidMount() {
        UserStore.addChangeListener(this._onChange);
        SystemStore.addChangeListener(this._onSystemChange);
        this._enableSystemPreferences();
    }

    componentWillUnmount() {
        UserStore.removeChangeListener(this._onChange);
        SystemStore.removeChangeListener(this._onSystemChange);
        PreferencesActions.registerCreateUserSystemPreferencesComponent(null);
    }

    public render() {
        var hasUserManage = PermissionService.checkPermission(UserPermissionNames.USER_MANAGE);
        var hasUserSettings = PermissionService.checkPermission(UserPermissionNames.USER_SETTINGS);
        var hasSSO = this.state.authenticationMode != AuthenticationModeEnum.LocalOnly

        var columns = [{
            field: "Email",
            headerText: InovxLoader.t('users:common.email'),
            type: "string",
            width: "auto"
        }, {
            field: "FirstName",
            headerText: InovxLoader.t('users:common.firstname'),
            type: "string"
        }, {
            field: "LastName",
            headerText: InovxLoader.t('users:common.lastname'),
            type: "string"
        }, {
            field: "Enabled",
            headerText: InovxLoader.t('users:common.userStatus'),
            type: "boolean",
            width: "140px",
            values: [
                { value: true, text: InovxLoader.t('users:common.enabled') },
                { value: false, text: InovxLoader.t('users:common.disabled') }
            ]
            }];

        var commands = {
            command: [
                {
                    iconClassName: "glyphicon glyphicon-pencil",
                    className: "grid-button",
                    click: this._beginEditUser
                }
            ],
            width: "85px",
            headerText: InovxLoader.t('users:common.commands')
        };

        var manageUserCommand = {
            iconClassName: "glyphicon glyphicon-trash",
            className: "grid-button",
            click: this._deleteUser
        }

        if (hasUserManage) {
            commands.command.push(manageUserCommand);
        }

        var defaultSort = [{
            field: "Email",
            dir: "ascending"
        }];

        var btns: any[] = [];

        var iconHeaderProps: any = {
            iconUrl: `${UrlUtilities.getBaseApplicationUrl()}/images/Users.png`,
            iconAtt: { alt: InovxLoader.t('users:header.registeredUsers') },
            title: InovxLoader.t('users:header.registeredUsers'),
            rcClass: "cd-btn add-btn",

        };

        if (hasUserManage) {
            btns.push({
                onClick: this._showAddUser,
                btnUrl: `${UrlUtilities.getBaseApplicationUrl()}/images/adduser.png`,
                btnAtt: {
                    alt: InovxLoader.t("users:header.newRegisteredUser"),
                    title: InovxLoader.t("users:header.newRegisteredUser"),
                    id: 'userApp-addUser-btn'
                },

            });
        }

        if (hasUserSettings) {
            btns.push({
                onClick: this._showSettings,
                btnUrl: `${UrlUtilities.getBaseApplicationUrl()}/images/Authenticationsettings.png`,
                btnAtt: {
                    alt: InovxLoader.t("users:settingsForm.headerTitle"),
                    title: InovxLoader.t("users:settingsForm.headerTitle"),
                    id: 'userApp-authen-settings-btn'
                },

            });
        }

        if (hasUserManage) {
            btns.push({
                onClick: this._synchUsers,
                btnUrl: `${UrlUtilities.getBaseApplicationUrl()}/images/SyncUsers.png`,
                btnAtt: {
                    alt: InovxLoader.t("users:header.syncUsers"),
                    title: InovxLoader.t("users:header.syncUsers"),
                    id: 'userApp-sync-users-btn'
                },

            });
        }

        iconHeaderProps.btnUrls = btns;

        return (
            <Inovx.Shared.Core.ReactLoader appReady={this.isAppLoaded() } errorStatus={this._errorStatus}>
                <div className="user-app-content">
                    <Inovx.Shared.Controls.If condition={this.state.isActionInProgress} class={'temp-modal-loader'}>
                        <div className="action-in-progress-loader-lrg"></div>
                    </Inovx.Shared.Controls.If>

                    <Inovx.Shared.Layout.IconHeader {...iconHeaderProps} />

                    <Inovx.Shared.Controls.Grid dataSource={this.state.allUsers} columns={columns} commands={commands} sort={true} filter={true}
                        pageable={{ pageCount: 5, pageSizes: [10, 20, 50], pageSize: 10 }} defaultSort={defaultSort}/>
                </div>
                <Inovx.Shared.Controls.ModalSlider isOpen={this.state.modalVisible} onClose={this._hideModal}>
                    {this.state.settingsShown ?
                    <UserSettings /> :
                        hasUserManage?<UserForm /> : <UserView />
                    }
                </Inovx.Shared.Controls.ModalSlider>
            </Inovx.Shared.Core.ReactLoader>
        );
    }
}

export default UserApp;
